<template>
  <div
    v-if="editorStatusMessage"
    class="editor-status-message"
    :class="{ 'editor-status-message--fullscreen': editorIsFullscreen }"
  >
    <NebulaToastMessage
      :type="messageType"
      ref="toastMessage"
      @close-toast-message="dismissMessage()"
    >
      <template #customContentOverride>
        <template
          v-if="editorStatusMessage.error"
        >
          {{ editorStatusMessage.error }}
        </template>
        <template
          v-if="editorStatusMessage.message === 'save-confirmation'"
        >
          <b>{{ draftName }}</b> has been successfully saved.
          <a
            v-if="myContentUrl"
            :href="myContentUrl"
          >
            <b>View in MyContent</b>
          </a>
        </template>
        <template
          v-if="editorStatusMessage.message === 'share-confirmation' && !editorStatusMessage.error"
        >
          {{ shareMessage }}
        </template>
        <template
          v-if="editorStatusMessage.message === 'publish-confirmation'"
        >
          <div class="editor-status-message--container">
            <div class="editor-status-message--container__title">
              {{ draft.name }}
            </div>
            {{ $t(' has been successfully saved.') }}
          </div>
          <div>
            <a
              :href="viewInContentUrl"
            >{{ $t('View in Atlas') }}</a>
          </div>
        </template>
      </template>
    </NebulaToastMessage>
    <!--
      Note: Other messages will end up getting added in the future here, like the
      success message for an editorial publish, which needs to send the user to the
      publishing queue
    -->
  </div>
</template>

<script>
import { NebulaToastMessage } from '@discoveryedu/nebula-components';
import { mapActions, mapState } from 'pinia';
import { getEnv, getDomains } from '@/lib/api';
import * as types from '@/lib/constants/store';
import { decodeHTML } from '@/lib/utils';

import {
  useEditorStore,
} from '@/stores';

export default {
  name: 'EditorStatusMessage',
  components: {
    NebulaToastMessage,
  },
  computed: {
    ...mapState(useEditorStore, [
      'draftType',
      'draftAssetId',
      'editorStatusMessage',
      'editorIsFullscreen',
      'draft',
    ]),
    draftName() {
      return decodeHTML(this.draft.name);
    },
    messageType() {
      return this.editorStatusMessage?.error ? 'error' : 'success';
    },
    shareMessage() {
      if (this.draftType === 'lesson') {
        return this.$t('Your Studio lesson has been shared.');
      }

      return this.draftType === 'board'
        ? this.$t('Your Studio board has been shared.')
        : this.$t('Your Studio slideshow has been shared.');
    },
    myContentUrl() {
      const { appDomain } = getDomains();
      if (this.draft.external_references) {
        // grab the my content id
        const myContentReference = this.draft.external_references.find(
          (ref) => (ref.reference_type === 'mycontent_folder'),
        );
        let url = 'suite/my-content/user';
        if (myContentReference) {
          url += `?folder_guid=${myContentReference.reference_id}`;
        }
        return appDomain.replace('learn', url);
      }
      return false;
    },
    viewInContentUrl() {
      // Studio content is authored in the US site rather than in a region-specific content site
      const currentEnv = getEnv();
      const appDomainUS = process.env[`VUE_APP_${currentEnv}_APPURL_US`];

      // Create a url like https://atlas.discoveryeducation.com/assets/<BOARD_ID>
      return appDomainUS.replace('app', 'atlas').replace('learn', `assets/${this.draftAssetId()}`);
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.SET_EDITOR_STATUS_MESSAGE,
    ]),
    dismissMessage() {
      this[types.SET_EDITOR_STATUS_MESSAGE](null);
    },
  },
};
</script>

<style lang="stylus">
.editor-status-message {
  align-items: center;
  display: flex;
  justify-content: right;
  position: absolute;
  top: 50px;
  right: 10px;
  width: 100%;
  z-index: 100000;

  &--fullscreen {
    margin-top: $nebula-space-2x;
  }

  &__block.comet-block-message {
    margin: 0 auto;
    position: relative;
    z-index: 800;
  }

  &__icon.comet-button__icon {
    height: $comet-size-icon-m;
    width: $comet-size-icon-m;
  }

  &--container {
    display: block;
    max-width: 330px;

    &__title {
      text-overflow: ellipsis;
      font-weight: 700;
      white-space: nowrap;
      overflow: hidden;
      display: inline-block;
      max-width: 100%;
      vertical-align: middle;
    }
  }
}
</style>
